/**
 * Gönderim kayıtları — kime hangi e-posta / SMS gitti, ulaştı mı.
 *
 * Tek soru: "Müşteri 'mail gelmedi' diyor — gitti mi?" Cevap bu listede:
 * tarih, kanal, alıcı, durum; gönderilemediyse sağlayıcının söylediği hata.
 *
 * Alıcı adresi KVKK kapsamındadır: `musteri.goruntule` yetkisi yoksa sunucu
 * maskeler (a***@ornek.com). Panel maske kararı vermez, sunucudan geleni gösterir.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useAdresParametreleri } from '../lib/adres';
import type { ColumnDef } from '@tanstack/react-table';
import { RotateCw } from 'lucide-react';
import { api, type GonderimKaydi, type DurumTonu } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { Girdi, Secim } from '../components/ui/Girdi';
import { Pencere } from '../components/ui/Pencere';
import { DurumRozeti } from '../components/DurumRozeti';
import { HataKutusu } from '../components/Durumlar';
import { VeriTablosu } from '../components/VeriTablosu';
import { tarihSaatYaz } from '../lib/format';
import { TanimSatiri } from '../components/ui/TanimSatiri';
import { TarihHucresi } from '../components/TarihHucresi';
import { sayfaBilgisi } from '../lib/sayfa';

function durumTonu(durum: GonderimKaydi['durum']): DurumTonu {
  if (durum === 'GONDERILDI') return 'success';
  if (durum === 'GECICI_HATA') return 'warning';
  return 'danger';
}

export function GonderimKayitlari() {
  const { parametreler, adresiGuncelle } = useAdresParametreleri();
  const [acik, setAcik] = useState<GonderimKaydi | null>(null);

  const kanal = parametreler.get('kanal') ?? '';
  const durum = parametreler.get('durum') ?? '';
  const q = parametreler.get('q') ?? '';
  const sayfaNo = Math.max(1, Number(parametreler.get('page') ?? '1') || 1);
  const sayfaBoyu = Number(parametreler.get('pageSize') ?? '25') || 25;
  const [aramaKutusu, setAramaKutusu] = useState(q);

  const { data, error, isPending, isFetching, refetch } = useQuery({
    queryKey: ['gonderimler', { kanal, durum, q, sayfaNo, sayfaBoyu }],
    queryFn: () =>
      api.gonderimler({
        page: sayfaNo,
        pageSize: sayfaBoyu,
        ...(kanal ? { kanal } : {}),
        ...(durum ? { durum } : {}),
        ...(q ? { q } : {}),
      }),
    placeholderData: (onceki) => onceki,
    refetchInterval: 30_000,
  });

  const kolonlar = useMemo<ColumnDef<GonderimKaydi, unknown>[]>(
    () => [
      {
        id: 'tarih',
        header: 'Ne zaman',
        enableSorting: false,
        cell: ({ row }) => <TarihHucresi zaman={row.original.tarih} />,
      },
      {
        id: 'kanal',
        header: 'Kanal',
        enableSorting: false,
        cell: ({ row }) => <span className="text-sm text-[var(--ink)]">{row.original.kanalEtiketi}</span>,
      },
      {
        id: 'alici',
        header: 'Alıcı',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <span className="rakam truncate text-sm text-[var(--ink)]">{row.original.alici}</span>
            {row.original.konu && (
              <span className="truncate text-xs text-[var(--ink-muted)]">{row.original.konu}</span>
            )}
          </div>
        ),
      },
      {
        id: 'durum',
        header: 'Durum',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-1">
            <DurumRozeti ton={durumTonu(row.original.durum)} etiket={row.original.durumEtiketi} />
            {row.original.hata && (
              <span className="line-clamp-1 max-w-xs text-xs text-[var(--ink-muted)]">
                {row.original.hata}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'fatura',
        header: 'İlgili fatura',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.faturaKodu ? (
            <Link
              to={`/faturalar/${encodeURIComponent(row.original.faturaKodu)}`}
              className="rakam text-sm text-[var(--accent)] hover:underline"
              onClick={(o) => o.stopPropagation()}
            >
              {row.original.faturaNumarasi ?? row.original.faturaKodu}
            </Link>
          ) : (
            <span className="text-xs text-[var(--ink-faint)]">—</span>
          ),
      },
    ],
    []
  );

  return (
    <Kabuk
      baslik="Gönderim kayıtları"
      kirintilar={[{ etiket: 'Denetim' }, { etiket: 'Gönderim kayıtları' }]}
      altBaslik={data ? `${data.total} kayıt` : 'Kime hangi e-posta / SMS gitti, ulaştı mı'}
      eylemler={
        <Dugme
          gorunum="ikincil"
          ikon={<RotateCw size={15} />}
          bekliyor={isFetching && !isPending}
          onClick={() => void refetch()}
        >
          Yenile
        </Dugme>
      }
    >
      <div className="kap uzuv flex flex-col gap-4 p-6">
        <form
          className="flex flex-col gap-3 @2xl:flex-row @2xl:items-end"
          onSubmit={(o) => {
            o.preventDefault();
            adresiGuncelle({ q: aramaKutusu.trim() });
          }}
        >
          <div className="min-w-0 flex-1">
            <Girdi
              etiket="Ara"
              type="search"
              value={aramaKutusu}
              onChange={(o) => setAramaKutusu(o.target.value)}
              placeholder="Alıcı, fatura kodu veya konu"
            />
          </div>
          <div className="@2xl:w-44">
            <Secim etiket="Kanal" value={kanal} onChange={(o) => adresiGuncelle({ kanal: o.target.value })}>
              <option value="">Tümü</option>
              <option value="MAIL">E-posta</option>
              <option value="SMS">SMS</option>
            </Secim>
          </div>
          <div className="@2xl:w-52">
            <Secim etiket="Durum" value={durum} onChange={(o) => adresiGuncelle({ durum: o.target.value })}>
              <option value="">Tümü</option>
              <option value="GONDERILDI">Gönderildi</option>
              <option value="GECICI_HATA">Geçici hata</option>
              <option value="KALICI_HATA">Gönderilemedi</option>
            </Secim>
          </div>
          <Dugme type="submit" gorunum="birincil">
            Ara
          </Dugme>
        </form>
      </div>

      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}

      <Kart>
        <VeriTablosu
          veri={data?.items ?? []}
          kolonlar={kolonlar}
          sayfa={sayfaBilgisi(data, sayfaNo, sayfaBoyu)}
          siralama={[]}
          yukleniyor={isPending}
          tazeleniyor={isFetching && !isPending}
          satirAnahtari={(s) => s.id}
          onSiralama={() => undefined}
          onSayfa={(yeni) => adresiGuncelle({ page: yeni > 1 ? yeni : undefined }, false)}
          onSayfaBoyu={(boy) => adresiGuncelle({ pageSize: boy })}
          onSatirTikla={(s) => setAcik(s)}
          bosBaslik="Henüz gönderim yok"
          bosAciklama="Müşteri bildirimi ve alarm e-postaları gönderilince burada görünür."
        />
      </Kart>

      {acik && (
        <Pencere
          acik
          baslik={`${acik.kanalEtiketi} · ${acik.durumEtiketi}`}
          aciklama={tarihSaatYaz(acik.tarih)}
          onKapat={() => setAcik(null)}
          altCubuk={
            <Dugme gorunum="ikincil" onClick={() => setAcik(null)}>
              Kapat
            </Dugme>
          }
        >
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 @xl:grid-cols-2">
            <TanimSatiri etiket="Alıcı" deger={acik.alici} />
            <TanimSatiri etiket="Şablon" deger={acik.sablon ?? '—'} />
            <TanimSatiri etiket="Kaynak" deger={acik.kaynak} />
            <TanimSatiri etiket="Sağlayıcı" deger={acik.saglayici ?? '—'} />
            {acik.konu && <TanimSatiri etiket="Konu" deger={acik.konu} />}
            {acik.faturaKodu && (
              <TanimSatiri etiket="Fatura" deger={acik.faturaNumarasi ?? acik.faturaKodu} />
            )}
          </dl>
          {acik.hata && (
            <div
              role="alert"
              className="mt-4 rounded-[var(--radius-md)] border border-[var(--state-bad)] bg-[var(--state-bad-soft)] p-3 text-sm text-[var(--ink)]"
            >
              {acik.hata}
            </div>
          )}
        </Pencere>
      )}
    </Kabuk>
  );
}
